import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import ListingCard from '../components/ListingCard';
import Loader from '../components/Loader';

interface Listing {
  id: string;
  title: string;
  price?: number;
  currency: string;
  images: string[];
  views: number;
  createdAt: string;
  user: {
    firstName: string;
    rating: number;
  };
}

export default function Listings() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'agriculture', icon: '🥬', label: 'Сельхоз' },
    { id: 'handmade', icon: '🎨', label: 'Хендмейд' },
    { id: 'tools', icon: '🔧', label: 'Инструменты' },
    { id: 'home', icon: '🏠', label: 'Для дома' },
    { id: 'electronics', icon: '📱', label: 'Электроника' }
  ];

  useEffect(() => {
    loadListings();
  }, [selectedCategory, searchQuery]);

  const loadListings = async () => {
    setIsLoading(true);
    try {
      const params: any = { status: 'active' };
      if (selectedCategory) params.category = selectedCategory;
      if (searchQuery) params.search = searchQuery;
      
      const response = await apiService.getListings(params);
      setListings(response.data.data || []);
    } catch (error) {
      console.error('Error loading listings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">🛍 Товары</h1>
        <Link to="/listings/create" className="btn-primary text-sm">
          ➕ Создать
        </Link>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="🔍 Поиск товаров..."
        className="input"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      {/* Categories */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`px-4 py-2 rounded-full whitespace-nowrap text-sm ${
            !selectedCategory
              ? 'bg-tg-theme-button text-tg-theme-button-text'
              : 'bg-tg-theme-secondary-bg'
          }`}
        >
          Все
        </button>
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-4 py-2 rounded-full whitespace-nowrap text-sm flex items-center gap-1 ${
              selectedCategory === cat.id
                ? 'bg-tg-theme-button text-tg-theme-button-text'
                : 'bg-tg-theme-secondary-bg'
            }`}
          >
            {cat.icon} {cat.label}
          </button>
        ))}
      </div>

      {/* Listings Grid */}
      {isLoading ? (
        <Loader className="py-8" />
      ) : listings.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-4xl mb-2">📭</p>
          <p className="text-tg-theme-hint">Объявлений пока нет</p>
          <Link to="/listings/create" className="btn-primary mt-4 inline-block">
            Создать первое
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {listings.map((listing) => (
            <ListingCard
              key={listing.id}
              {...listing}
            />
          ))}
        </div>
      )}
    </div>
  );
}
